import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { ActionIcon, AppShell, Box, Button, Checkbox, Container, Flex, Paper, Popover, ScrollArea, Text, TextInput, ThemeIcon, Title, Loader, Tooltip, Switch } from "@mantine/core"
import { IconSend, IconRobot, IconInfoCircle, IconSparkles, IconFiles, IconChevronDown, IconCircleCheck, IconClock, IconAlertTriangle, IconX, IconSearch, IconFileText, IconPlus } from "@tabler/icons-react"
import "@mantine/core/styles.css"
import { ChatAssistantResponse, DocumentPickerOption, DocumentPickerPayload } from "../models/responses/ChatAssistantResponse"
import { toast } from "react-toastify"
import { config } from "../config"
import { AnswerRequest } from "../models/requests/AnswerRequest"
import { loadUserChatHistoryBySessionId, generateAndUpdateSessionTitle } from "../redux/actions/assistantActions"
import { useAppDispatch } from "../redux/hooks";
import { useSelector } from "react-redux";
import { useSession } from "../contexts/SessionContext"
import 'katex/dist/katex.min.css';
import FormulaRenderer from "../components/FormulaRenderer"
import { useAuth } from "../customAuth/authStateHook";
import { TOKEN_KEY } from "../customAuth/authConstants";
import CollectionDropdown from "../components/CollectionDropdown";
import { CollectionResponse } from "../models/responses/CollectionResponse";
import TextAreaInput from "../components/common/TextAreaInput";
import { extractPdfInfo } from "../infrastructures/utils";
import { getCollectionDocuments } from "../api/assistantApi";

type DocumentPickerAction = "selected" | "all_above" | "none_global";

type DocumentSelectionPayload = {
    action: DocumentPickerAction;
    type?: string;
    selectionMode?: string;
    originalQuestion: string;
    documents: DocumentPickerOption[];
};

const COMPLETED_STATUS_VALUES = new Set(["3", "completed"]);
const SELECTED_DOCUMENTS_SCROLL_THRESHOLD = 5;
const COLOR = "#1c1c1a";

const getDocumentStatusLabel = (status?: string | number | null) => {
    if (status === null || status === undefined || status === "") {
        return "Status not specified";
    }

    const normalized = String(status).trim();
    const statusMap: Record<string, string> = {
        "0": "Not started",
        "1": "In progress",
        "2": "Processed",
        "3": "Completed",
        "4": "Failed",
        "5": "Cancelled",
        "6": "Chunks created",
        "7": "Failed to convert",
        "8": "Failed to create chunks",
        "9": "Failed to ingest",
    };

    return statusMap[normalized] || normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const isCompletedDocument = (status?: string | number | null) => {
    if (status === null || status === undefined || status === "") {
        return false;
    }

    return COMPLETED_STATUS_VALUES.has(String(status).trim().toLowerCase());
};

const DocumentStatusIcon = ({ status }: { status?: string | number | null }) => {
    const label = getDocumentStatusLabel(status);
    const completed = isCompletedDocument(status);
    const normalized = String(status ?? "").trim().toLowerCase();
    const failed = normalized.includes("failed") || ["4", "7", "8", "9"].includes(normalized);
    const inProgress = normalized.includes("progress") || normalized === "1";
    const Icon = completed ? IconCircleCheck : failed ? IconAlertTriangle : inProgress ? IconClock : IconFileText;
    const color = completed ? "#2f8a4f" : failed ? "#b54708" : inProgress ? "#8a6a4a" : "#8f8578";

    return (
        <Tooltip label={label} withArrow>
            <ThemeIcon size={18} radius="xl" variant="light" style={{ color, background: completed ? "#e6f4ea" : "#f1ede6", flexShrink: 0 }}>
                <Icon size={12} />
            </ThemeIcon>
        </Tooltip>
    );
};

const parseDocumentPickerAnswer = (answer: string, metadata?: any): { answer: string; picker: DocumentPickerPayload | null } => {
    if (metadata?.type === "document_picker" && metadata.document_picker) {
        const picker = metadata.document_picker as DocumentPickerPayload;
        return { answer: picker.message || answer, picker };
    }

    const marker = "[DOC_PICKER]";
    if (!answer?.startsWith(marker)) {
        return { answer, picker: null };
    }

    try {
        const picker = JSON.parse(answer.substring(marker.length)) as DocumentPickerPayload;
        return { answer: picker.message, picker };
    } catch (error) {
        console.error("Failed to parse persisted document picker", error);
        return { answer, picker: null };
    }
};

function ChatAssistantContent() {
    const viewport = useRef<HTMLDivElement>(null)
    const isProcessingRef = useRef<boolean>(false)
    const [messages, setMessages] = useState<ChatAssistantResponse[]>([])
    const questionRef = useRef<string>("")
    const [isProcessing, setIsProcessing] = useState<boolean>(false)
    const { userConversations } = useSelector((state: any) => state.assistant);
    const { user } = useAuth();
    const { currentSessionId, markSessionAsUsed, currentSessionCollectionId, updateSessionCollectionId } = useSession();
    const [isLoading, setIsLoading] = useState(false);
    const [searchCompleted, setSearchCompleted] = useState(false);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(currentSessionCollectionId);
    const { collections, systemCollectionId } = useSelector((state: any) => state.collection);
    const dispatch = useAppDispatch();
    const activeStreamingSessionRef = useRef<string | null>(null);
    const [hasInputContent, setHasInputContent] = useState(false);
    const [pdfInfo, setPdfInfo] = useState<{ url?: string; pages: number[] } | null>(null);
    const [relevanceThreshold] = useState(50);
    const relevanceThresholdRef = useRef(relevanceThreshold);
    const [useAgenticRag] = useState(true);
    const [isDocumentScopePickerOpen, setIsDocumentScopePickerOpen] = useState(false);
    const [isLoadingCollectionDocuments, setIsLoadingCollectionDocuments] = useState(false);
    const [collectionDocuments, setCollectionDocuments] = useState<DocumentPickerOption[]>([]);
    const [selectedScopeHashcodes, setSelectedScopeHashcodes] = useState<string[]>([]);
    const [documentScopeFilter, setDocumentScopeFilter] = useState("");
    const [isAssistantOptionsOpen, setIsAssistantOptionsOpen] = useState(false);
    const KNOWLEDGE_ENRICHMENT_KEY = "mindvault_enrich_with_general_knowledge";
    const [enrichWithGeneralKnowledge, setEnrichWithGeneralKnowledge] = useState<boolean>(() => {
        const stored = localStorage.getItem(KNOWLEDGE_ENRICHMENT_KEY);
        return stored === null ? true : stored === "true";
    });

    const handleToggleKnowledgeEnrichment = useCallback(() => {
        setEnrichWithGeneralKnowledge((prev) => {
            const next = !prev;
            localStorage.setItem(KNOWLEDGE_ENRICHMENT_KEY, String(next));
            return next;
        });
    }, []);

    const userScrolledUpRef = useRef<boolean>(false);
    const autoScrollEnabledRef = useRef<boolean>(true);

    const [progressMessage, setProgressMessage] = useState<string>("");
    const [progressElapsed, setProgressElapsed] = useState<string>("");

    const messagesCacheRef = useRef<Record<string, ChatAssistantResponse[]>>({});
    const currentSessionIdRef = useRef<string | null>(currentSessionId);
    const sessionsAwaitingTitleRef = useRef<Set<string>>(new Set());
    const firstQuestionSnapshotRef = useRef<Record<string, ChatAssistantResponse | null>>({});

    useEffect(() => {
        currentSessionIdRef.current = currentSessionId;
    }, [currentSessionId]);

    const updateMessagesForSession = useCallback((sessionId: string | null, updater: (prev: ChatAssistantResponse[]) => ChatAssistantResponse[]) => {
        if (!sessionId) {
            setMessages([]);
            return;
        }

        setMessages((currentMessages) => {
            const isActiveSession = currentSessionIdRef.current === sessionId;
            const baseMessages = isActiveSession
                ? currentMessages
                : (messagesCacheRef.current[sessionId] || []);
            const updatedMessages = updater(baseMessages);
            messagesCacheRef.current[sessionId] = updatedMessages;
            return isActiveSession ? updatedMessages : currentMessages;
        });
    }, []);

    // Memoize the selected collection to prevent unnecessary re-calculations
    const selectedCollection = useMemo(() => {
        if (selectedCollectionId) {
            return collections.find((collection: CollectionResponse) =>
                collection.Id === selectedCollectionId
            ) || systemCollectionId;
        }
        return null;
    }, [selectedCollectionId, collections, systemCollectionId]);

    const completedCollectionDocuments = useMemo(
        () => collectionDocuments.filter((document) => isCompletedDocument(document.status)),
        [collectionDocuments]
    );

    const selectedScopeDocuments = useMemo(
        () => completedCollectionDocuments.filter((document) => selectedScopeHashcodes.includes(document.hashcode)),
        [completedCollectionDocuments, selectedScopeHashcodes]
    );

    const visibleCollectionDocuments = useMemo(() => {
        const filter = documentScopeFilter.trim().toLowerCase();
        const baseList = filter
            ? collectionDocuments.filter((document) => document.filename.toLowerCase().includes(filter))
            : completedCollectionDocuments.length > 10
                ? completedCollectionDocuments
                : collectionDocuments;

        return baseList;
    }, [collectionDocuments, completedCollectionDocuments, documentScopeFilter]);

    const shouldShowDocumentScopeSearch = collectionDocuments.length > 10 || completedCollectionDocuments.length > 10;

    useEffect(() => {
        setSelectedCollectionId(currentSessionCollectionId || systemCollectionId);
        questionRef.current = "";
        setHasInputContent(false);
        setIsDocumentScopePickerOpen(false);
        setSelectedScopeHashcodes([]);
        setDocumentScopeFilter("");
    }, [currentSessionId, currentSessionCollectionId, systemCollectionId]);


    useEffect(() => {
        relevanceThresholdRef.current = relevanceThreshold;
    }, [relevanceThreshold]);

    useEffect(() => {
        setCollectionDocuments([]);
        setSelectedScopeHashcodes([]);
        setDocumentScopeFilter("");
        setIsDocumentScopePickerOpen(false);
    }, [selectedCollectionId]);

    useEffect(() => {
        setProgressMessage("");
        setProgressElapsed("");
        setQuestionRef("");

        if (!currentSessionId) {
            messagesCacheRef.current = {};
            setMessages([]);
            setSearchCompleted(true);
            return;
        }

        const cachedMessages = messagesCacheRef.current[currentSessionId];
        if (cachedMessages !== undefined) {
            setMessages(cachedMessages);
            setSearchCompleted(true);
        } else {
            updateMessagesForSession(currentSessionId, () => []);
            setSearchCompleted(false);
        }
    }, [currentSessionId, updateMessagesForSession]);

    useEffect(() => {
        if (currentSessionId) {
            const hasCachedSession = Object.prototype.hasOwnProperty.call(messagesCacheRef.current, currentSessionId);
            setIsLoading(!hasCachedSession);

            dispatch(loadUserChatHistoryBySessionId(currentSessionId))
                .then(() => {
                    setIsLoading(false);
                    setSearchCompleted(true);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
            setSearchCompleted(true);
        }
    }, [currentSessionId, dispatch]);

    useEffect(() => {
        if (!isLoading && searchCompleted) {
            handleUserConversations(userConversations);
        }
    }, [userConversations, isLoading, searchCompleted]);

    useEffect(() => {
        const handleScroll = () => {
            if (!viewport.current) return;

            const { scrollTop, scrollHeight, clientHeight } = viewport.current;
            const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;

            // If user scrolls up, disable auto-scroll
            if (!isAtBottom && autoScrollEnabledRef.current) {
                userScrolledUpRef.current = true;
                autoScrollEnabledRef.current = false;
            }

            // If user scrolls back to bottom, re-enable auto-scroll
            if (isAtBottom && userScrolledUpRef.current) {
                userScrolledUpRef.current = false;
                autoScrollEnabledRef.current = true;
            }
        };

        const viewportElement = viewport.current;
        if (viewportElement) {
            viewportElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (viewportElement) {
                viewportElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    useEffect(() => {
        if (viewport.current && autoScrollEnabledRef.current) {
            viewport.current.scrollTo({ top: viewport.current.scrollHeight, behavior: "smooth" })
        }
    }, [messages]);

    const handleDocumentScopeToggle = useCallback(async () => {
        if (!selectedCollection?.VectorStoreCollectionName) {
            toast.error("Please select a source collection first.");
            return;
        }

        if (isDocumentScopePickerOpen) {
            setIsDocumentScopePickerOpen(false);
            return;
        }

        setIsDocumentScopePickerOpen(true);

        if (collectionDocuments.length > 0) {
            return;
        }

        setIsLoadingCollectionDocuments(true);
        const documents = await getCollectionDocuments(selectedCollection.VectorStoreCollectionName);
        setCollectionDocuments(documents);
        setIsLoadingCollectionDocuments(false);
    }, [collectionDocuments.length, isDocumentScopePickerOpen, selectedCollection?.VectorStoreCollectionName]);

    const clearDocumentScopeSelection = useCallback(() => {
        setSelectedScopeHashcodes([]);
        setDocumentScopeFilter("");
    }, []);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (isProcessingRef.current || hasActiveDocumentPicker) {
            return;
        }

        if (!questionRef.current.trim()) {
            toast.error("Please enter a question.");
            return;
        }

        if (!selectedCollectionId || !selectedCollection?.VectorStoreCollectionName) {
            toast.error("Please select a source collection first.");
            return;
        }

        setProgressMessage("");
        setProgressElapsed("");

        userScrolledUpRef.current = false;
        autoScrollEnabledRef.current = true;

        // Set processing state synchronously
        isProcessingRef.current = true;
        setIsProcessing(true);

        if (!currentSessionId) {
            toast.error("Unable to determine the active session.");
            return;
        }

        const submittedQuestion = questionRef.current.trim();
        const upfrontDocumentSelection: DocumentSelectionPayload | undefined = selectedScopeDocuments.length > 0
            ? {
                action: "selected",
                originalQuestion: submittedQuestion,
                documents: selectedScopeDocuments,
            }
            : undefined;
        const newMessage = new ChatAssistantResponse({
            Question: submittedQuestion,
            Answer: "",
            OrderIndex: messages.length + 1,
            ChatGptAnswer: "",
            MetadataJson: upfrontDocumentSelection
                ? {
                    type: "document_selection",
                    document_selection: upfrontDocumentSelection,
                }
                : null,
        });

        const cachedMessagesForSession = messagesCacheRef.current[currentSessionId] || [];
        const isFirstMessageInSession = cachedMessagesForSession.length === 0;

        updateMessagesForSession(currentSessionId, (prev) => [...prev, newMessage]);

        if (isFirstMessageInSession) {
            sessionsAwaitingTitleRef.current.add(currentSessionId);
            firstQuestionSnapshotRef.current[currentSessionId] = newMessage;
        }

        await markSessionAsUsed(currentSessionId);

        const chat_history = buildChatHistory(userConversations);
        const answerRequest = new AnswerRequest(questionRef.current, user?.email || "", chat_history);
        setQuestionRef("");
        setIsDocumentScopePickerOpen(false);
        setDocumentScopeFilter("");
        setSelectedScopeHashcodes([]);

        try {
            handleStreamingResponse(
                answerRequest,
                selectedCollectionId,
                currentSessionId,
                currentSessionCollectionId || null,
                upfrontDocumentSelection
            );
        } catch (error) {
            console.error("Error fetching QA results", error);
            toast.error("No data has been found. Please try again.");
            isProcessingRef.current = false;
            setIsProcessing(false);
        }
    };

    const handleUserConversations = useCallback((userConversations: ChatAssistantResponse[]) => {
        if (!currentSessionId) {
            return;
        }

        if (userConversations && userConversations.length > 0) {
            const previousConversations = userConversations.map((item: any, index: number) => {
                const parsedPicker = parseDocumentPickerAnswer(item.Answer, item.MetadataJson);
                return {
                    Question: item.Question,
                    Answer: parsedPicker.answer,
                    ConversationId: item.ConversationId,
                    OrderIndex: index + 1,
                    ChatGptAnswer: item.ChatGptAnswer,
                    DocumentPicker: parsedPicker.picker,
                    MetadataJson: item.MetadataJson || null,
                };
            });
            const conversationsWithPickerSelections = previousConversations.map((conversation, index, allConversations) => {
                if (!conversation.DocumentPicker) {
                    return conversation;
                }

                const selection = allConversations
                    .slice(index + 1)
                    .find((candidate: any) =>
                        candidate.MetadataJson?.type === "document_selection" &&
                        candidate.MetadataJson?.document_selection?.originalQuestion === conversation.DocumentPicker?.originalQuestion
                    )?.MetadataJson?.document_selection;

                if (!selection?.documents) {
                    return conversation;
                }

                return {
                    ...conversation,
                    DocumentPicker: {
                        ...conversation.DocumentPicker,
                        selectedHashcodes: selection.documents.map((document: DocumentPickerOption) => document.hashcode),
                    },
                };
            });
            updateMessagesForSession(currentSessionId, () => conversationsWithPickerSelections);
        } else {
            updateMessagesForSession(currentSessionId, () => []);
        }
    }, [currentSessionId, updateMessagesForSession]);


    const buildChatHistory = useCallback((userConversations: ChatAssistantResponse[]): string => {
        const lastThreeConversations = userConversations.slice(-3);
        if (lastThreeConversations.length) {
            return lastThreeConversations
                .map(
                    (conversation: ChatAssistantResponse) => {
                        const answer = conversation.DocumentPicker
                            ? conversation.DocumentPicker.message
                            : conversation.Answer;
                        return `Question: ${conversation.Question}\nAnswer: ${answer}`;
                    }
                )
                .join("\n\n");
        }
        return "";
    }, []);

    const calculateRelevanceThreshold = () => {
        const threshold = relevanceThresholdRef.current;
        if (threshold === 0) {
            return 0.1;
        }
        return threshold / 100.0;
    }


    const handleStreamingResponse = useCallback(async (
        answerRequest: AnswerRequest,
        collectionId?: number | null,
        sessionIdForRequest?: string | null,
        sessionCollectionIdAtSubmission?: number | null,
        documentSelection?: DocumentSelectionPayload
    ) => {
        const requestSessionId = sessionIdForRequest || currentSessionId;

        if (!requestSessionId) {
            console.error('No current session ID available');
            setIsProcessing(false);
            return;
        }

        const sessionCollectionSnapshot = sessionCollectionIdAtSubmission ?? null;
        activeStreamingSessionRef.current = requestSessionId;

        let finalAnswerAccumulator = "";
        let chatGptAnswer = "";

        // --- Smooth, decoupled text reveal ----------------------------------
        // SSE chunks arrive in irregular bursts (sometimes many tokens at
        // once, sometimes one at a time). Pushing whatever arrived straight to
        // the UI on a timer makes the text jump in visible chunks. Instead we
        // keep the fully-received text as a target and reveal it toward the
        // user at a steady, adaptive pace via requestAnimationFrame.
        let targetText = "";        // everything received from the stream so far
        let displayedChars = 0;     // how much of targetText is on screen
        let rafId: number | null = null;
        let lastFrameTime = 0;
        let lastPushTime = 0;

        // Baseline typing speed. The reveal speeds up automatically when a
        // large backlog builds (so the UI never lags far behind the stream)
        // but stays smooth frame-to-frame.
        const MIN_CHARS_PER_SECOND = 120;
        const CATCH_UP_SECONDS = 0.4;
        // Cap UI commits to ~30fps: FormulaRenderer re-parses the whole answer
        // on every render, so committing every animation frame would jank.
        const MIN_PUSH_INTERVAL = 33;

        const pushDisplayedText = () => {
            if (activeStreamingSessionRef.current !== requestSessionId) {
                return;
            }
            const visibleText = targetText.slice(0, displayedChars);
            updateMessagesForSession(requestSessionId, (prevMessages) =>
                prevMessages.map((msg, index) =>
                    index === prevMessages.length - 1
                        ? { ...msg, Answer: visibleText }
                        : msg
                )
            );
        };

        const animateReveal = (now: number) => {
            if (activeStreamingSessionRef.current !== requestSessionId) {
                rafId = null;
                return;
            }

            const remaining = targetText.length - displayedChars;
            if (remaining <= 0) {
                // Caught up — flush the tail in case the last push was throttled.
                pushDisplayedText();
                rafId = null;
                return;
            }

            const deltaSeconds = lastFrameTime ? (now - lastFrameTime) / 1000 : 0;
            lastFrameTime = now;

            const charsPerSecond = Math.max(
                MIN_CHARS_PER_SECOND,
                remaining / CATCH_UP_SECONDS
            );
            const charsToAdd = Math.max(1, Math.ceil(charsPerSecond * deltaSeconds));
            displayedChars = Math.min(targetText.length, displayedChars + charsToAdd);

            if (now - lastPushTime >= MIN_PUSH_INTERVAL) {
                lastPushTime = now;
                pushDisplayedText();
            }

            rafId = requestAnimationFrame(animateReveal);
        };

        const ensureAnimating = () => {
            if (rafId === null) {
                lastFrameTime = 0;
                rafId = requestAnimationFrame(animateReveal);
            }
        };

        // Reveal everything immediately. Used when the stream ends, errors, or
        // a payload replaces the answer wholesale, so the final text is never
        // left truncated by the in-flight animation.
        const finalizeReveal = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            displayedChars = targetText.length;
        };

        const params = new URLSearchParams({
            question: answerRequest.Question,
            userEmail: user?.email || "",
            sessionId: requestSessionId,
            collectionName: selectedCollection?.VectorStoreCollectionName || "",
            min_relevance_score: calculateRelevanceThreshold().toString(),
        });

        if (documentSelection) {
            params.set("document_selection", JSON.stringify(documentSelection));
        }
        params.set("enrich_with_general_knowledge", enrichWithGeneralKnowledge.toString());

        try {
            const token = localStorage.getItem(TOKEN_KEY);

            if (!token) {
                toast.error("Authentication required. Please login again.");
                setIsProcessing(false);
                return;
            }

            const response = await fetch(`${config.pythonApiUrl}/assistant/get-answer?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'text/event-stream',
                    'Cache-Control': 'no-cache',
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    toast.error("Session expired. Please login again.");
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('No reader available');
            }

            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    finalizeReveal();
                    pushDisplayedText();

                    if (activeStreamingSessionRef.current === requestSessionId) {
                        if (currentSessionIdRef.current === requestSessionId) {
                            setProgressMessage("");
                            setProgressElapsed("");
                        }
                        isProcessingRef.current = false;
                        setIsProcessing(false);
                    }
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.substring(6);

                        if (data.startsWith('[PROGRESS')) {
                            const matchWithTime = data.match(/\[PROGRESS:(\d+\.?\d*)s\] (.+)/);

                            if (matchWithTime && currentSessionIdRef.current === requestSessionId) {
                                const elapsed = matchWithTime[1];
                                const message = matchWithTime[2];
                                setProgressMessage(message);
                                setProgressElapsed(elapsed);
                            } else {
                                const matchWithoutTime = data.match(/\[PROGRESS\] (.+)/);
                                if (matchWithoutTime && currentSessionIdRef.current === requestSessionId) {
                                    const message = matchWithoutTime[1];
                                    setProgressMessage(message);
                                    setProgressElapsed("");
                                }
                            }
                            continue;
                        }

                        if (data === '[CHATGPT_LOADING]') {
                            if (activeStreamingSessionRef.current === requestSessionId) {
                                updateMessagesForSession(requestSessionId, (prevMessages) =>
                                    prevMessages.map((msg, index) =>
                                        index === prevMessages.length - 1
                                            ? { ...msg, IsChatGptLoading: true }
                                            : msg
                                    )
                                );
                            }
                            continue;
                        }

                        if (data.startsWith('[START_CHATGPT]') && data.includes('[END_CHATGPT]')) {
                            const match = data.match(/\[START_CHATGPT\]([\s\S]*?)\[END_CHATGPT\]/);
                            if (match && activeStreamingSessionRef.current === requestSessionId) {
                                chatGptAnswer = match[1];
                                updateMessagesForSession(requestSessionId, (prevMessages) =>
                                    prevMessages.map((msg, index) =>
                                        index === prevMessages.length - 1
                                            ? {
                                                ...msg,
                                                ChatGptAnswer: chatGptAnswer,
                                                IsChatGptLoading: false
                                            }
                                            : msg
                                    )
                                );
                            }
                            continue;
                        }

                        if (data.startsWith('[DOC_PICKER]')) {
                            try {
                                const pickerPayload = JSON.parse(data.substring('[DOC_PICKER]'.length)) as DocumentPickerPayload;
                                finalAnswerAccumulator = pickerPayload.message;
                                chatGptAnswer = "";
                                targetText = finalAnswerAccumulator;
                                finalizeReveal();
                                if (activeStreamingSessionRef.current === requestSessionId) {
                                    setProgressMessage("");
                                    setProgressElapsed("");
                                    updateMessagesForSession(requestSessionId, (prevMessages) =>
                                        prevMessages.map((msg, index) =>
                                            index === prevMessages.length - 1
                                                ? {
                                                    ...msg,
                                                    Answer: pickerPayload.message,
                                                    DocumentPicker: pickerPayload,
                                                    MetadataJson: {
                                                        type: "document_picker",
                                                        document_picker: pickerPayload,
                                                    },
                                                }
                                                : msg
                                        )
                                    );
                                }
                            } catch (error) {
                                console.error("Failed to parse document picker payload", error);
                            }
                            continue;
                        }

                        if (data.startsWith('[SCOPE]')) {
                            try {
                                const scopePayload = JSON.parse(data.substring('[SCOPE]'.length));
                                if (activeStreamingSessionRef.current === requestSessionId) {
                                    updateMessagesForSession(requestSessionId, (prevMessages) =>
                                        prevMessages.map((msg, index) =>
                                            index === prevMessages.length - 1
                                                ? {
                                                    ...msg,
                                                    MetadataJson: {
                                                        ...(msg.MetadataJson || {}),
                                                        type: msg.MetadataJson?.type || "scope_metadata",
                                                        scope: scopePayload,
                                                    },
                                                }
                                                : msg
                                        )
                                    );
                                }
                            } catch (error) {
                                console.error("Failed to parse scope metadata payload", error);
                            }
                            continue;
                        }

                        if (data === "[DONE]") {
                            finalizeReveal();

                            if (activeStreamingSessionRef.current === requestSessionId) {
                                if (currentSessionIdRef.current === requestSessionId) {
                                    setProgressMessage("");
                                    setProgressElapsed("");
                                }
                                isProcessingRef.current = false;
                                setIsProcessing(false);

                                const shouldGenerateTitle = sessionsAwaitingTitleRef.current.has(requestSessionId);
                                const firstMessageSnapshot = firstQuestionSnapshotRef.current[requestSessionId];
                                const info = extractPdfInfo(finalAnswerAccumulator);
                                if (currentSessionIdRef.current === requestSessionId) {
                                    setPdfInfo(info);
                                }

                                updateMessagesForSession(requestSessionId, (prevMessages) => {
                                    return prevMessages.map((msg, index) =>
                                        index === prevMessages.length - 1
                                            ? {
                                                ...msg,
                                                Answer: finalAnswerAccumulator,
                                                ChatGptAnswer: chatGptAnswer
                                            }
                                            : msg
                                    );
                                });

                                if (shouldGenerateTitle && firstMessageSnapshot && user?.email) {
                                    const chatAssistantResponse = new ChatAssistantResponse({
                                        Question: firstMessageSnapshot.Question,
                                        Answer: finalAnswerAccumulator,
                                        ChatGptAnswer: chatGptAnswer,
                                        SourceUrl: info?.url || null,
                                        Pages: info?.pages || [],
                                        OrderIndex: firstMessageSnapshot.OrderIndex || 1,
                                    });

                                    Promise.resolve(
                                        dispatch(generateAndUpdateSessionTitle(
                                            requestSessionId,
                                            chatAssistantResponse,
                                            user.email,
                                            collectionId || null
                                        ))
                                    ).finally(() => {
                                        sessionsAwaitingTitleRef.current.delete(requestSessionId);
                                        delete firstQuestionSnapshotRef.current[requestSessionId];
                                    });
                                } else if (shouldGenerateTitle) {
                                    sessionsAwaitingTitleRef.current.delete(requestSessionId);
                                    delete firstQuestionSnapshotRef.current[requestSessionId];
                                }

                                if (!sessionCollectionSnapshot && collectionId) {
                                    updateSessionCollectionId(requestSessionId, collectionId);
                                }
                            }
                            return;
                        }

                        const isConsultedSourcesProgress =
                            progressMessage.toLowerCase().includes("building consulted sources") ||
                            progressMessage.toLowerCase().includes("building citations");

                        if (
                            activeStreamingSessionRef.current === requestSessionId &&
                            currentSessionIdRef.current === requestSessionId &&
                            !isConsultedSourcesProgress
                        ) {
                            setProgressMessage("");
                            setProgressElapsed("");
                        }

                        finalAnswerAccumulator += data;
                        targetText = finalAnswerAccumulator;
                        ensureAnimating();
                    }
                }
            }

        } catch (error) {
            finalizeReveal();

            console.error('Streaming error:', error);
            if (activeStreamingSessionRef.current === requestSessionId) {
                isProcessingRef.current = false;
                setIsProcessing(false);
                toast.error("Something went wrong. Please try again.");
            }
        }
    }, [currentSessionId, user?.email, selectedCollection, dispatch, updateSessionCollectionId, updateMessagesForSession, calculateRelevanceThreshold, progressMessage, enrichWithGeneralKnowledge]);

    const handleDocumentPickerSubmit = useCallback((
        picker: DocumentPickerPayload,
        action: DocumentPickerAction,
        documents: DocumentPickerOption[]
    ) => {
        if (!currentSessionId || isProcessingRef.current) {
            return;
        }

        const maxSelect = picker.maxSelect;
        const isLimitedSelection = typeof maxSelect === "number" && maxSelect > 0;
        if (isLimitedSelection && documents.length > maxSelect) {
            toast.warning(`Selecteaza maximum ${maxSelect} documente pentru aceasta analiza.`);
            return;
        }

        const displayQuestion =
            action === "none_global"
                ? "None, search in the entire collection"
                : action === "all_above"
                    ? "All from above"
                    : picker.selectionMode === "document_analysis_file_selection"
                        ? `Analizeaza: ${documents.map((document) => document.filename).join(", ")}`
                        : `Cauta in: ${documents.map((document) => document.filename).join(", ")}`;

        const newMessage = new ChatAssistantResponse({
            Question: displayQuestion,
            Answer: "",
            OrderIndex: messages.length + 1,
            ChatGptAnswer: "",
        });

        const selectedHashcodes = documents.map((document) => document.hashcode);
        const selectionPayload: DocumentSelectionPayload = {
            action,
            type: picker.type,
            selectionMode: picker.selectionMode,
            originalQuestion: picker.originalQuestion,
            documents,
        };
        newMessage.MetadataJson = {
            type: "document_selection",
            document_selection: selectionPayload,
        };
        updateMessagesForSession(currentSessionId, (prev) => [
            ...prev.map((message) =>
                message.DocumentPicker === picker
                    ? {
                        ...message,
                        DocumentPicker: {
                            ...picker,
                            selectedHashcodes,
                        },
                    }
                    : message
            ),
            newMessage,
        ]);
        isProcessingRef.current = true;
        setIsProcessing(true);

        const answerRequest = new AnswerRequest(displayQuestion, user?.email || "", "");

        handleStreamingResponse(
            answerRequest,
            selectedCollectionId,
            currentSessionId,
            currentSessionCollectionId || null,
            selectionPayload
        );
    }, [
        currentSessionId,
        currentSessionCollectionId,
        handleStreamingResponse,
        messages.length,
        selectedCollectionId,
        updateMessagesForSession,
        user?.email,
    ]);

    // Memoize the empty state content
    const emptyStateContent = useMemo(() => (
        <Flex direction="column" align="center" justify="center" h="calc(100vh - 270px)" gap="md">
            <ThemeIcon size={80} radius={40} color="#8a6a4a" variant="light">
                <IconRobot size={40} />
            </ThemeIcon>
            <Title order={2}>How can I help you today?</Title>
            <Text c="dimmed" ta="center" maw={500}>
                Ask me anything and I'll do my best to assist you. I can answer questions, provide information, or
                just chat with you.
            </Text>
        </Flex>
    ), []);

    const loadingContent = useMemo(() => (
        <Flex direction="column" align="center" justify="center" h="calc(100vh - 240px)" gap="md">
            <Loader size="lg" color="#8a6a4a" />
            <Text c="dimmed" ta="center">
                Loading session...
            </Text>
        </Flex>
    ), []);

    const setQuestionRef = useCallback((value: string) => {
        questionRef.current = value;

        // Only update state when transitioning between empty and non-empty
        const isEmpty = value.trim() === "";
        const wasEmpty = !hasInputContent;

        if (isEmpty !== wasEmpty) {
            setHasInputContent(!isEmpty);
        }

    }, [hasInputContent]);

    const hasActiveDocumentPicker = messages.length > 0 && Boolean(messages[messages.length - 1]?.DocumentPicker);

    return (
        <>
            <div
                style={{
                    padding: 0,
                }}
                className="assistant-main-container"
            >
                <Box className="collection-dropdown-navbar-container" style={{ display: 'none' }}>
                    <CollectionDropdown
                        setSelectedCollectionId={setSelectedCollectionId}
                        selectedCollectionId={selectedCollectionId}
                    />
                </Box>

                <AppShell padding={0}>
                    <AppShell.Main>
                        <Container size="unset" h="100%">
                            <Paper
                                radius="md"
                                p={0}
                                style={{
                                    height: "100vh",
                                    width: "100%",
                                    display: "flex",
                                    margin: "auto",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                                className="no-shadow"
                            >

                                <ScrollArea
                                    style={{ flex: 1, minHeight: 0, marginBottom: '2rem' }}
                                    type="always"
                                    className="scroll-area-chat-assistant"
                                    scrollbarSize={8}
                                    viewportRef={viewport}
                                    offsetScrollbars
                                >
                                    <Box
                                        p={{ base: 'xs', sm: 'sm', md: 'md' }}
                                        w="100%"
                                        m='auto'
                                        style={{ maxWidth: '810px' }}
                                    >
                                        {isLoading && !searchCompleted ? loadingContent
                                            : messages.length === 0 && searchCompleted ? emptyStateContent
                                                : messages.map((message: any, index: number) => (
                                                    <MessageBubble
                                                        key={message.OrderIndex}
                                                        message={message}
                                                        isFirstMessage={index === 0}
                                                        progressMessage={index === messages.length - 1 && isProcessing ? progressMessage : undefined}
                                                        progressElapsed={index === messages.length - 1 && isProcessing ? progressElapsed : undefined}
                                                        useAgenticRag={useAgenticRag}
                                                        onDocumentPickerSubmit={handleDocumentPickerSubmit}
                                                        isDocumentPickerDisabled={index < messages.length - 1 || isProcessing}
                                                    />
                                                ))
                                        }
                                    </Box>
                                </ScrollArea>

                                <Box>
                                    <Box style={{ maxWidth: '810px', margin: '0 auto', padding: '0 20px 4px 12px' }}>
                                        <form onSubmit={handleSubmit}>
                                            <Box
                                                style={{
                                                    background: '#fbfaf7',
                                                    border: '1px solid #ded8cf',
                                                    borderRadius: '22px',
                                                    boxShadow: '0 10px 28px rgba(28,28,26,0.08)',
                                                    overflow: 'hidden',
                                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                }}
                                                className="chat-input-card"
                                            >
                                                <TextAreaInput
                                                    value={questionRef.current}
                                                    disabled={hasActiveDocumentPicker}
                                                    onChange={setQuestionRef}
                                                    onSubmit={handleSubmit as any}
                                                    placeholder="How can I help you today?"
                                                />

                                                {isDocumentScopePickerOpen && (
                                                    <Box
                                                        mx="12px"
                                                        mb="8px"
                                                        p="10px"
                                                        style={{
                                                            border: '1px solid #e4ded4',
                                                            borderRadius: '12px',
                                                            background: '#fbfaf7',
                                                        }}
                                                    >
                                                        <Flex align="center" justify="space-between" mb="xs" gap="sm">
                                                            <Text size="sm" fw={600} style={{ color: '#2a2824' }}>
                                                                Select documents for the next question
                                                            </Text>
                                                            <ActionIcon
                                                                variant="subtle"
                                                                size="sm"
                                                                color="gray"
                                                                onClick={() => setIsDocumentScopePickerOpen(false)}
                                                                disabled={isProcessing}
                                                            >
                                                                <IconX size={14} />
                                                            </ActionIcon>
                                                        </Flex>

                                                        {isLoadingCollectionDocuments ? (
                                                            <Flex align="center" gap="xs" py="sm">
                                                                <Loader size="xs" color="#8a6a4a" />
                                                                <Text size="sm" c="dimmed">Loading documents...</Text>
                                                            </Flex>
                                                        ) : (
                                                            <>
                                                                {shouldShowDocumentScopeSearch && (
                                                                    <TextInput
                                                                        value={documentScopeFilter}
                                                                        onChange={(event) => setDocumentScopeFilter(event.currentTarget.value)}
                                                                        placeholder="Filter documents by name..."
                                                                        size="xs"
                                                                        mb="xs"
                                                                        leftSection={<IconSearch size={13} />}
                                                                        disabled={isProcessing}
                                                                    />
                                                                )}

                                                                <Flex
                                                                    direction="column"
                                                                    gap="xs"
                                                                    style={{
                                                                        maxHeight: "240px",
                                                                        overflowY: "auto",
                                                                        paddingRight: "4px",
                                                                    }}
                                                                >
                                                                    {visibleCollectionDocuments.map((option) => {
                                                                        const completed = isCompletedDocument(option.status);
                                                                        return (
                                                                            <Flex key={option.hashcode} align="center" gap="xs">
                                                                                <DocumentStatusIcon status={option.status} />
                                                                                <Checkbox
                                                                                    label={option.filename}
                                                                                    checked={selectedScopeHashcodes.includes(option.hashcode)}
                                                                                    disabled={isProcessing || !completed}
                                                                                    onChange={(event) => {
                                                                                        const checked = event.currentTarget.checked;
                                                                                        setSelectedScopeHashcodes((current) =>
                                                                                            checked
                                                                                                ? [...current, option.hashcode]
                                                                                                : current.filter((hashcode) => hashcode !== option.hashcode)
                                                                                        );
                                                                                    }}
                                                                                    color="#8a6a4a"
                                                                                    styles={{
                                                                                        root: { flex: 1, minWidth: 0 },
                                                                                        label: {
                                                                                            color: completed ? '#374151' : '#aaa39a',
                                                                                            fontSize: '13px',
                                                                                            lineHeight: 1.45,
                                                                                            wordBreak: 'break-word',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            </Flex>
                                                                        );
                                                                    })}
                                                                </Flex>

                                                                <Flex align="center" justify="space-between" mt="xs" gap="sm" wrap="wrap">
                                                                    <Text size="xs" c="dimmed">
                                                                        Selected: {selectedScopeDocuments.length} / {completedCollectionDocuments.length}
                                                                    </Text>
                                                                    <Button
                                                                        size="xs"
                                                                        variant="subtle"
                                                                        color="gray"
                                                                        disabled={isProcessing || selectedScopeDocuments.length === 0}
                                                                        onClick={clearDocumentScopeSelection}
                                                                    >
                                                                        Clear selection
                                                                    </Button>
                                                                </Flex>
                                                            </>
                                                        )}
                                                    </Box>
                                                )}

                                                <Flex align="center" justify="space-between" px="16px" pb="12px" pt="8px" gap="sm">
                                                    <Flex align="center" gap="6px" wrap="wrap">
                                                        <Popover
                                                            opened={isAssistantOptionsOpen}
                                                            onChange={setIsAssistantOptionsOpen}
                                                            position="top-start"
                                                            offset={8}
                                                            shadow="lg"
                                                            radius="md"
                                                            width={294}
                                                            withinPortal
                                                        >
                                                            <Popover.Target>
                                                                <ActionIcon
                                                                    type="button"
                                                                    size={34}
                                                                    radius="xl"
                                                                    variant="default"
                                                                    disabled={isProcessing || hasActiveDocumentPicker}
                                                                    onClick={() => setIsAssistantOptionsOpen((opened) => !opened)}
                                                                    aria-label="Open assistant options"
                                                                    style={{
                                                                        background: '#ffffff',
                                                                        borderColor: isDocumentScopePickerOpen || selectedScopeDocuments.length > 0 || enrichWithGeneralKnowledge ? '#d6cfc3' : '#e4ded4',
                                                                        color: '#5d564c',
                                                                        boxShadow: '0 1px 2px rgba(28,28,26,0.05)',
                                                                    }}
                                                                >
                                                                    <IconPlus size={18} />
                                                                </ActionIcon>
                                                            </Popover.Target>
                                                            <Popover.Dropdown
                                                                style={{
                                                                    padding: '8px',
                                                                    background: '#282724',
                                                                    borderColor: '#4a4741',
                                                                    boxShadow: '0 18px 40px rgba(28,28,26,0.24)',
                                                                }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    disabled={isProcessing || hasActiveDocumentPicker}
                                                                    onClick={() => {
                                                                        handleDocumentScopeToggle();
                                                                        setIsAssistantOptionsOpen(false);
                                                                    }}
                                                                    style={{
                                                                        width: '100%',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        background: 'transparent',
                                                                        color: '#f4f1eb',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '12px',
                                                                        padding: '10px 9px',
                                                                        cursor: isProcessing || hasActiveDocumentPicker ? 'not-allowed' : 'pointer',
                                                                        opacity: isProcessing || hasActiveDocumentPicker ? 0.55 : 1,
                                                                        textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <IconFiles size={18} style={{ flexShrink: 0 }} />
                                                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                                                        <Text size="sm" fw={600} style={{ color: '#f4f1eb', lineHeight: 1.25 }}>
                                                                            Select documents
                                                                        </Text>
                                                                        <Text size="xs" style={{ color: '#bdb7ad', lineHeight: 1.35 }}>
                                                                            {selectedScopeDocuments.length > 0
                                                                                ? `${selectedScopeDocuments.length} selected for the next question`
                                                                                : "Choose specific sources for the next question"}
                                                                        </Text>
                                                                    </Box>
                                                                    <IconChevronDown size={14} style={{ color: '#bdb7ad', flexShrink: 0 }} />
                                                                </button>

                                                                <Box my={6} style={{ height: 1, background: '#4a4741' }} />

                                                                <button
                                                                    type="button"
                                                                    disabled={isProcessing || hasActiveDocumentPicker}
                                                                    onClick={handleToggleKnowledgeEnrichment}
                                                                    style={{
                                                                        width: '100%',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        background: 'transparent',
                                                                        color: '#f4f1eb',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '12px',
                                                                        padding: '10px 9px',
                                                                        cursor: isProcessing || hasActiveDocumentPicker ? 'not-allowed' : 'pointer',
                                                                        opacity: isProcessing || hasActiveDocumentPicker ? 0.55 : 1,
                                                                        textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <IconSparkles size={18} style={{ flexShrink: 0, color: enrichWithGeneralKnowledge ? '#e7c27a' : '#bdb7ad' }} />
                                                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                                                        <Text size="sm" fw={600} style={{ color: '#f4f1eb', lineHeight: 1.25 }}>
                                                                            General knowledge
                                                                        </Text>
                                                                        <Text size="xs" style={{ color: '#bdb7ad', lineHeight: 1.35 }}>
                                                                            {enrichWithGeneralKnowledge
                                                                                ? "Adds clearly labeled technical context"
                                                                                : "Answer only from your documents"}
                                                                        </Text>
                                                                    </Box>
                                                                    <Switch
                                                                        size="xs"
                                                                        checked={enrichWithGeneralKnowledge}
                                                                        readOnly
                                                                        color="#8a6a4a"
                                                                        style={{ pointerEvents: 'none', flexShrink: 0 }}
                                                                    />
                                                                </button>
                                                            </Popover.Dropdown>
                                                        </Popover>
                                                        <Tooltip
                                                            label="Select a knowledge collection to search answers from. Cannot be changed after a conversation has a collection."
                                                            position="top-start"
                                                            withArrow
                                                            multiline
                                                            w={260}
                                                            styles={{
                                                                tooltip: {
                                                                    fontSize: '12px',
                                                                    lineHeight: '1.5',
                                                                    background: '#1c1c1a',
                                                                    color: '#f4f1eb',
                                                                    borderRadius: '8px',
                                                                    padding: '8px 12px',
                                                                },
                                                                arrow: { background: '#1c1c1a' },
                                                            }}
                                                        >
                                                            <IconInfoCircle
                                                                size={15}
                                                                style={{ color: '#b0a89e', cursor: 'default', flexShrink: 0 }}
                                                            />
                                                        </Tooltip>
                                                        <CollectionDropdown
                                                            setSelectedCollectionId={setSelectedCollectionId}
                                                            selectedCollectionId={selectedCollectionId}
                                                            compact
                                                        />
                                                    </Flex>

                                                    <ActionIcon
                                                        type="submit"
                                                        size={36}
                                                        radius="xl"
                                                        className="chat-send-button"
                                                        disabled={isProcessing || hasActiveDocumentPicker || !hasInputContent}
                                                        style={{
                                                            background: (isProcessing || hasActiveDocumentPicker || !hasInputContent) ? '#ece8e0' : '#1c1c1a',
                                                            color: (isProcessing || hasActiveDocumentPicker || !hasInputContent) ? '#b0a89e' : '#faf8f4',
                                                            border: 'none',
                                                            transition: 'background 0.15s ease, color 0.15s ease',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {isProcessing
                                                            ? <Loader size={14} style={{ color: '#b0a89e' }} />
                                                            : <IconSend size={15} style={{ marginLeft: '1px' }} />
                                                        }
                                                    </ActionIcon>
                                                </Flex>
                                            </Box>
                                        </form>

                                        <Text ta="center" mt="10px" style={{ fontSize: '11px', color: '#c0b8ae' }}>
                                            The assistant can make mistakes. Please double-check.
                                        </Text>
                                    </Box>
                                </Box>
                            </Paper>
                        </Container>
                    </AppShell.Main>
                </AppShell>
            </div>
        </>
    );
}

const cleanAnswer = (text: string): string => {
    if (!text) return '';
    return text
        .replace(/\[CHATGPT_LOADING\]/g, '')
        .replace(/<\s*br\s*\/?\s*>/gi, '<br/>')
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/(?:\s*<br\/>\s*){3,}/g, '<br/><br/>')
        .replace(/(?:\s*<br\/>\s*)+$/g, '')
        .replace(/^(?:\s*<br\/>\s*)+/g, '')
        .replace(/^---\s*$/gm, '')
        .replace(/^---<br\/>$/gm, '')
        .replace(/<br\/>---<br\/>/g, '<br/>')
        .replace(/(?:\s*<br\/>\s*)+$/g, '')
        .trim();
};

const MessageBubble = React.memo(({ message, isFirstMessage = false, progressMessage, progressElapsed, useAgenticRag, onDocumentPickerSubmit, isDocumentPickerDisabled }: { message: ChatAssistantResponse, isFirstMessage?: boolean, progressMessage?: string, progressElapsed?: string, useAgenticRag: boolean, onDocumentPickerSubmit: (picker: DocumentPickerPayload, action: DocumentPickerAction, documents: DocumentPickerOption[]) => void, isDocumentPickerDisabled: boolean }) => {
    const [showChatGptAnswer, setShowChatGptAnswer] = useState(false);
    const [isChatGptLoading, setIsChatGptLoading] = useState(message.IsChatGptLoading || false);
    const [isQuestionScopeExpanded, setIsQuestionScopeExpanded] = useState(false);
    const [selectedDocumentHashcodes, setSelectedDocumentHashcodes] = useState<string[]>(
        message.DocumentPicker?.selectedHashcodes || []
    );

    const [documentPickerFilter, setDocumentPickerFilter] = useState("");

    // Sync loading state when message prop changes
    useEffect(() => {
        setIsChatGptLoading(message.IsChatGptLoading || false);
    }, [message.IsChatGptLoading]);

    useEffect(() => {
        if (message.DocumentPicker?.selectedHashcodes) {
            setSelectedDocumentHashcodes(message.DocumentPicker.selectedHashcodes);
        }
    }, [message.DocumentPicker?.selectedHashcodes]);

    // Check if ChatGPT answer is available
    const hasChatGptAnswer = message.ChatGptAnswer && message.ChatGptAnswer.trim().length > 0;
    const filteredPickerOptions = message.DocumentPicker?.options.filter((option) =>
        option.filename.toLowerCase().includes(documentPickerFilter.trim().toLowerCase())
    ) || [];
    const selectedDocuments = message.DocumentPicker?.options.filter((option) =>
        selectedDocumentHashcodes.includes(option.hashcode)
    ) || [];
    const questionScopedDocuments = message.MetadataJson?.type === "document_selection"
        ? message.MetadataJson.document_selection?.documents || []
        : [];
    const shouldScrollQuestionScope = questionScopedDocuments.length > SELECTED_DOCUMENTS_SCROLL_THRESHOLD;
    const referencedScope = message.MetadataJson?.scope;
    const referencedDocuments = referencedScope?.documents || [];
    const hasReferencedScope = referencedDocuments.length > 0;
    const documentPickerMaxSelect = message.DocumentPicker?.maxSelect;
    const hasDocumentPickerLimit = typeof documentPickerMaxSelect === "number" && documentPickerMaxSelect > 0;
    const isDocumentAnalysisPicker = message.DocumentPicker?.selectionMode === "document_analysis_file_selection";
    const hasReachedDocumentPickerLimit = Boolean(
        hasDocumentPickerLimit && selectedDocuments.length >= (documentPickerMaxSelect || 0)
    );
    const documentPickerSelectedLabel = hasDocumentPickerLimit
        ? `Selected: ${selectedDocuments.length} / ${documentPickerMaxSelect}`
        : `Selected: ${selectedDocuments.length} / ${filteredPickerOptions.length}`;
    const documentPickerPrimaryLabel = isDocumentAnalysisPicker ? "Analyze selected" : "Search in selected";

    return (
        <div key={message.OrderIndex} style={{ marginBottom: '32px', paddingTop: isFirstMessage ? '10px' : 0 }}>
            {/* User Question */}
            <Flex justify="flex-end" mb="16px">
                <Box
                    className="chat-question-card"
                    style={{
                        maxWidth: '72%',
                        background: 'linear-gradient(135deg, #1c1c1a 0%, #2a2824 100%)',
                        color: '#f4f1eb',
                        borderRadius: '20px 20px 6px 20px',
                        padding: isFirstMessage ? '14px 16px 12px' : '12px 16px',
                        fontSize: '14.5px',
                        lineHeight: '1.7',
                        border: '1px solid rgba(244, 241, 235, 0.08)',
                        boxShadow: 'none',
                    }}
                >
                    <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#f4f1eb", fontSize: '14.5px', lineHeight: 1.7 }}>
                        {message.Question}
                    </Text>
                    {questionScopedDocuments.length > 0 && (
                        <Box
                            mt="8px"
                            pt="8px"
                            style={{
                                borderTop: '1px solid rgba(244, 241, 235, 0.16)',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setIsQuestionScopeExpanded((current) => !current)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: '100%',
                                    padding: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#d9d3c9',
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontFamily: "'Instrument Sans', sans-serif",
                                }}
                            >
                                <IconChevronDown
                                    size={13}
                                    style={{
                                        transform: isQuestionScopeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.15s ease',
                                        flexShrink: 0,
                                    }}
                                />
                                Selected documents ({questionScopedDocuments.length})
                            </button>
                            {isQuestionScopeExpanded && (
                                <Flex
                                    direction="column"
                                    gap={3}
                                    mt={3}
                                    style={{
                                        maxHeight: shouldScrollQuestionScope ? '110px' : undefined,
                                        overflowY: shouldScrollQuestionScope ? 'auto' : undefined,
                                        paddingRight: shouldScrollQuestionScope ? '4px' : undefined,
                                    }}
                                >
                                    {questionScopedDocuments.map((document) => (
                                        <Text
                                            key={document.hashcode}
                                            style={{
                                                color: '#f4f1eb',
                                                fontSize: '12px',
                                                lineHeight: 1.45,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {document.filename}
                                        </Text>
                                    ))}
                                </Flex>
                            )}
                        </Box>
                    )}
                </Box>
            </Flex>

            {/* Assistant Answer */}
            <Flex align="flex-start" gap="10px">
                <Box
                    className="chat-answer-card"
                    style={{
                        width: '100%',
                        flex: 1,
                        minWidth: 0,
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(250,247,241,0.98) 100%)',
                        border: '1px solid #e6dfd3',
                        borderRadius: '20px 20px 20px 8px',
                        padding: '14px 16px 16px',
                        boxShadow: '0 10px 30px rgba(83, 62, 34, 0.08)',
                    }}
                >
                    {/* Combined row: Documentation/ChatGPT buttons (left) + Scope pill (right) */}
                    {(((hasChatGptAnswer || isChatGptLoading) && !!cleanAnswer(message.Answer.trim()))) && (
                        <Flex
                            align="center"
                            justify="space-between"
                            mb="md"
                            pb="sm"
                            gap="xs"
                            style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                            {/* Left: Documentation / ChatGPT toggle buttons */}
                            {(hasChatGptAnswer || isChatGptLoading) && cleanAnswer(message.Answer.trim()) ? (
                                <Flex gap="xs" wrap="wrap">
                                    <Tooltip label="Răspuns bazat pe documentația încărcată">
                                        <Button
                                            size="xs"
                                            variant={!showChatGptAnswer ? "filled" : "subtle"}
                                            color={COLOR}
                                            leftSection={<IconRobot size={14} />}
                                            onClick={() => setShowChatGptAnswer(false)}
                                            style={{
                                                fontSize: "11px",
                                                height: "28px",
                                            }}
                                        >
                                            Documentation
                                        </Button>
                                    </Tooltip>

                                    <Tooltip label="Răspuns ChatGPT (cunoștințe generale)">
                                        <Button
                                            size="xs"
                                            variant={showChatGptAnswer ? "filled" : "subtle"}
                                            color={COLOR}
                                            leftSection={isChatGptLoading ? <Loader size={14} color={COLOR} /> : <IconSparkles size={14} />}
                                            onClick={() => hasChatGptAnswer && setShowChatGptAnswer(true)}
                                            disabled={isChatGptLoading}
                                            style={{
                                                fontSize: "11px",
                                                height: "28px",
                                            }}
                                        >
                                            ChatGPT
                                        </Button>
                                    </Tooltip>
                                </Flex>
                            ) : <span />}

                            {/* Right: Scope pill */}
                            {/* {hasReferencedScope && (
                                <Popover position="bottom-end" shadow="md" radius="md" width={320} withinPortal>
                                    <Popover.Target>
                                        <button
                                            type="button"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '5px 9px',
                                                border: '1px solid #ded8cf',
                                                borderRadius: '999px',
                                                background: '#fbfaf7',
                                                color: '#5d564c',
                                                fontSize: '12px',
                                                lineHeight: 1.3,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontFamily: "'Instrument Sans', sans-serif",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <IconFiles size={13} />
                                            Scope: {referencedDocuments.length} referenced doc{referencedDocuments.length === 1 ? "" : "s"}
                                            {referencedScope?.expanded_to_full_collection ? " · expanded" : ""}
                                            <IconChevronDown size={12} />
                                        </button>
                                    </Popover.Target>
                                    <Popover.Dropdown
                                        style={{
                                            padding: '10px',
                                            borderColor: '#ded8cf',
                                            background: '#ffffff',
                                        }}
                                    >
                                        <Text size="xs" fw={700} mb={6} style={{ color: '#2a2824' }}>
                                            Referenced documents searched
                                        </Text>
                                        <Flex
                                            direction="column"
                                            gap={5}
                                            style={{
                                                maxHeight: '220px',
                                                overflowY: referencedDocuments.length > 6 ? 'auto' : undefined,
                                                paddingRight: referencedDocuments.length > 6 ? '4px' : undefined,
                                            }}
                                        >
                                            {referencedDocuments.map((document) => (
                                                <Flex key={document.hashcode || document.filename} align="flex-start" gap={6}>
                                                    <DocumentStatusIcon status={document.status} />
                                                    <Text size="xs" style={{ color: '#4b5563', lineHeight: 1.35, wordBreak: 'break-word' }}>
                                                        {document.filename}
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Flex>
                                        {referencedScope?.expanded_to_full_collection && (
                                            <Text
                                                size="xs"
                                                mt={8}
                                                pt={8}
                                                style={{
                                                    color: '#7c6f63',
                                                    lineHeight: 1.4,
                                                    borderTop: '1px solid #eee8dd',
                                                }}
                                            >
                                                Search expanded to the full collection because the referenced document{referencedDocuments.length === 1 ? "" : "s"} identified did not contain enough evidence.
                                            </Text>
                                        )}
                                    </Popover.Dropdown>
                                </Popover>
                            )} */}
                            {/* Toggle Button - Show if ChatGPT answer exists OR is loading */}

                        </Flex>
                    )}
                    {/* Answer Content */}
                    {message.Answer || message.ChatGptAnswer ? (
                        <div
                            style={{
                                fontSize: "14px",
                                lineHeight: "1.7",
                                color: "#374151",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                            }}
                        >
                            {showChatGptAnswer && hasChatGptAnswer ? (
                                <>
                                    <FormulaRenderer text={cleanAnswer(message.ChatGptAnswer || "")} />
                                    <Flex
                                        align="center"
                                        gap="xs"
                                        mt="md"
                                        pt="sm"
                                        style={{
                                            borderTop: "1px solid #e5e7eb",
                                            fontSize: "13px",
                                            color: "#6b7280"
                                        }}
                                    >
                                        <IconSparkles size={14} />
                                        <Text size="xs" c="dimmed">
                                            Answer generated based on ChatGPT general knowledge
                                        </Text>
                                    </Flex>
                                </>
                            ) : (
                                <>
                                    <FormulaRenderer text={cleanAnswer(message.Answer)} />
                                    {message.DocumentPicker && (
                                        <Box mt="md">
                                            {filteredPickerOptions?.length > 10 && <TextInput
                                                value={documentPickerFilter}
                                                onChange={(event) => setDocumentPickerFilter(event.currentTarget.value)}
                                                placeholder="Filter by name..."
                                                size="xs"
                                                mb="xs"
                                                disabled={isDocumentPickerDisabled}
                                            />}
                                            <Flex
                                                direction="column"
                                                gap="xs"
                                                style={{
                                                    maxHeight: "260px",
                                                    overflowY: "auto",
                                                    paddingRight: "4px",
                                                }}
                                            >
                                                {filteredPickerOptions.map((option) => (
                                                    (() => {
                                                        const isSelected = selectedDocumentHashcodes.includes(option.hashcode);
                                                        return (
                                                            <Checkbox
                                                                key={option.hashcode}
                                                                label={option.filename}
                                                                checked={isSelected}
                                                                disabled={isDocumentPickerDisabled || (!isSelected && hasReachedDocumentPickerLimit)}
                                                                onChange={(event) => {
                                                                    const checked = event.currentTarget.checked;
                                                                    setSelectedDocumentHashcodes((current) =>
                                                                        checked
                                                                            ? hasDocumentPickerLimit && current.length >= (documentPickerMaxSelect || 0)
                                                                                ? current
                                                                                : [...current, option.hashcode]
                                                                            : current.filter((hashcode) => hashcode !== option.hashcode)
                                                                    );
                                                                }}
                                                                color="#8a6a4a"
                                                            />
                                                        );
                                                    })()
                                                ))}
                                            </Flex>
                                            <Text size="xs" c="dimmed" mt="xs">
                                                {documentPickerSelectedLabel}
                                                {isDocumentAnalysisPicker ? " documents for analysis" : ""}
                                            </Text>
                                            <Flex gap="xs" mt="sm" wrap="wrap">
                                                <Button
                                                    size="xs"
                                                    color="#8a6a4a"
                                                    disabled={isDocumentPickerDisabled || selectedDocuments.length === 0}
                                                    onClick={() => onDocumentPickerSubmit(message.DocumentPicker!, "selected", selectedDocuments)}
                                                >
                                                    {documentPickerPrimaryLabel}
                                                </Button>
                                                {!isDocumentAnalysisPicker && (
                                                    <>
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="#8a6a4a"
                                                            disabled={isDocumentPickerDisabled}
                                                            onClick={() => onDocumentPickerSubmit(message.DocumentPicker!, "all_above", message.DocumentPicker!.options)}
                                                        >
                                                            Search in all results
                                                        </Button>
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            color="gray"
                                                            disabled={isDocumentPickerDisabled}
                                                            onClick={() => onDocumentPickerSubmit(message.DocumentPicker!, "none_global", message.DocumentPicker!.options)}
                                                        >
                                                            None, search in the entire collection
                                                        </Button>
                                                    </>
                                                )}
                                            </Flex>
                                        </Box>
                                    )}
                                    {progressMessage && (
                                        <Flex
                                            align="center"
                                            gap="xs"
                                            mt="md"
                                            pt="sm"
                                            style={{
                                                borderTop: "1px solid #e5e7eb",
                                            }}
                                        >
                                            <Loader size="xs" color="#8a6a4a" />
                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                <Text size="sm" c="dimmed">{progressMessage}</Text>
                                                {progressElapsed && (
                                                    <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                                                        {progressElapsed}s elapsed
                                                    </Text>
                                                )}
                                            </div>
                                        </Flex>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <Flex align="center" gap="xs">
                            {progressMessage ? (
                                // Show progress update
                                <>
                                    <Loader size="xs" color="#8a6a4a" />
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <Text size="sm" c="dimmed">{progressMessage}</Text>
                                        {progressElapsed && (
                                            <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
                                                {progressElapsed}s elapsed
                                            </Text>
                                        )}
                                    </div>
                                </>
                            ) : (
                                // Default thinking state
                                <>
                                    <Loader size="xs" color="#8a6a4a" />
                                    {!useAgenticRag ? (
                                        <Text size="sm" c="dimmed">Thinking...</Text>
                                    ) : (
                                        <Text size="sm" c="dimmed">Sending request to Agent...</Text>
                                    )}
                                </>
                            )}
                        </Flex>
                    )}
                </Box>
            </Flex>
        </div>
    );
});


export default function ChatAssistantView() {
    return <ChatAssistantContent />;
}

